// shared/guards/home/home.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AuthService } from 'app/services/auth/auth.service';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_HOME } from '../../services/feature-flags/feature-flags.constants';
import { DEO_AI_ASSIST_URL, LandingRouteService } from '../../services/landing-route/landing-route.service';

/**
 * Guard for the /home route. When the home feature flag is disabled, the user is sent to their
 * landing route instead (/deo-ai-assist for a DEO-only user, otherwise /start). A DEO-only user
 * also skips home when the flag is on, since there is nothing for them there.
 * Unlike FeatureGuard (which redirects to /home on failure), this guard targets the home route
 * itself, so it must redirect elsewhere to avoid a redirect loop.
 */
@Injectable({
  providedIn: 'root'
})
export class HomeGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly featureFlagsService: FeatureFlagsService,
    private readonly landingRouteService: LandingRouteService
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    // Required, do not remove: initial navigation and the auth app initializer run
    // concurrently (see appConfig), so this await is what makes routing wait for auth
    // to settle. Without it the guard reads an unauthenticated service on cold start.
    await this.authService.ready;
    await this.featureFlagsService.ensureFeatureFlagsLoaded();

    if (!this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_HOME)) {
      return this.router.parseUrl(this.landingRouteService.resolveLandingUrl());
    }

    if (this.landingRouteService.isEntitledToDeoOnly()) {
      return this.router.parseUrl(DEO_AI_ASSIST_URL);
    }

    return true;
  }
}
